import type { TaskDto, TaskPriority, TaskStatus } from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useScope } from '../lib/scope';
import { useUnsavedChanges } from '../lib/useUnsavedChanges';
import { TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, fromDateInput, toDateInput } from '../lib/format';
import { AttachmentPicker, toChoices, toRefs, type AttachmentChoice } from './Attachments';
import { OrgUnitPicker } from './OrgUnitPicker';
import { PersonPicker } from './PersonPicker';
import { Button, Dialog, Field, Input, Select, Textarea, useToast } from './ui';

/**
 * Aufgaben-Dialog.
 *
 * Standardfall bleibt einfach: Was? Wer? Bis wann? Fuer welchen Bereich?
 * Alles Weitere ist hinter "Weitere Angaben" versteckt.
 */
export function TaskDialog({
  open,
  onClose,
  task,
  defaultUnitIds,
}: {
  open: boolean;
  onClose: () => void;
  task?: TaskDto | null;
  defaultUnitIds?: string[];
}): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [startAt, setStartAt] = useState('');
  const [status, setStatus] = useState<TaskStatus>('OPEN');
  const [priority, setPriority] = useState<TaskPriority>('NORMAL');
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [tags, setTags] = useState('');
  const [checklist, setChecklist] = useState('');
  const [recurrence, setRecurrence] = useState('NONE');
  const [attachments, setAttachments] = useState<AttachmentChoice[]>([]);
  const [advanced, setAdvanced] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { setErrors({}); }, [title, description, dueAt, startAt, status, priority, unitIds, assignees, tags, checklist, recurrence]);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (task) {
      setTitle(task.title);
      setDescription(task.description ?? '');
      setDueAt(toDateInput(task.dueAt));
      setStartAt(toDateInput(task.startAt));
      setStatus(task.status);
      setPriority(task.priority);
      setUnitIds(task.organisationUnits.map((u) => u.id));
      setAssignees(task.assignees.map((a) => a.id));
      setTags(task.tags.join(', '));
      setRecurrence(task.recurrence);
      setChecklist('');
      setAttachments(toChoices(task.attachments));
      setAdvanced(
        task.priority !== 'NORMAL' || task.recurrence !== 'NONE' || task.tags.length > 0
          || task.attachments.length > 0,
      );
    } else {
      setTitle('');
      setDescription('');
      setDueAt('');
      setStartAt('');
      setStatus('OPEN');
      setPriority('NORMAL');
      setUnitIds(defaultUnitIds ?? (scope.unitId ? [scope.unitId] : []));
      setAssignees([]);
      setTags('');
      setChecklist('');
      setRecurrence('NONE');
      setAttachments([]);
      setAdvanced(false);
    }
  }, [open, task, defaultUnitIds, scope.unitId]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        description: description || null,
        status,
        priority,
        dueAt: fromDateInput(dueAt, true),
        startAt: fromDateInput(startAt),
        organisationUnitIds: unitIds,
        assigneePersonIds: assignees,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        recurrence,
        attachments: toRefs(attachments),
        ...(task
          ? {}
          : {
              checklist: checklist
                .split('\n')
                .map((c) => c.trim())
                .filter(Boolean),
            }),
      };
      return task
        ? api.patch<TaskDto>(`/api/tasks/${task.id}`, payload)
        : api.post<TaskDto>('/api/tasks', payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show(task ? 'Aufgabe gespeichert' : 'Aufgabe angelegt');
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors();
        setErrors(fields);
        if (Object.keys(fields).some((key) => !['title', 'dueAt', 'organisationUnitIds', 'assigneePersonIds'].includes(key))) setAdvanced(true);
        toast.show(error.message, 'error');
      } else {
        toast.show('Speichern fehlgeschlagen', 'error');
      }
    },
  });

  const dirty = title !== (task?.title ?? '') || description !== (task?.description ?? '')
    || dueAt !== toDateInput(task?.dueAt ?? null) || startAt !== toDateInput(task?.startAt ?? null)
    || status !== (task?.status ?? 'OPEN') || priority !== (task?.priority ?? 'NORMAL')
    || recurrence !== (task?.recurrence ?? 'NONE') || tags !== (task?.tags.join(', ') ?? '') || checklist !== ''
    || JSON.stringify(unitIds) !== JSON.stringify(task?.organisationUnits.map((u) => u.id) ?? defaultUnitIds ?? (scope.unitId ? [scope.unitId] : []))
    || JSON.stringify(assignees) !== JSON.stringify(task?.assignees.map((a) => a.id) ?? [])
    || JSON.stringify(toRefs(attachments)) !== JSON.stringify(toRefs(task ? toChoices(task.attachments) : []));
  const requestClose = useUnsavedChanges(open, dirty, onClose, save.isPending);
  const submit = (): void => {
    const fields: Record<string, string> = {};
    if (!title.trim()) fields.title = 'Bitte beschreibe kurz, was zu tun ist.';
    if (!unitIds.length) fields.organisationUnitIds = 'Bitte wähle mindestens einen Bereich aus.';
    if (startAt && dueAt && startAt > dueAt) {
      fields.startAt = 'Das Startdatum darf nicht nach dem Fälligkeitsdatum liegen.';
      setAdvanced(true);
    }
    setErrors(fields);
    if (Object.keys(fields).length === 0) save.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      title={task ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={requestClose} disabled={save.isPending}>
            Abbrechen
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Aufgabe speichern
          </Button>
        </>
      }
    >
      <fieldset disabled={save.isPending} className="space-y-4">
        <p className="text-sm text-slate-600">Was ist zu tun und für welchen Bereich? Alle weiteren Angaben kannst du später ergänzen.</p>
        {Object.keys(errors).length > 0 && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">Bitte prüfe die markierten Angaben. Deine Eingaben bleiben erhalten.</p>}
        <Field label="Was ist zu tun?" required error={errors['title']}>
          {(id) => (
            <Input
              id={id}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Helfer für das Heimspiel organisieren"
              autoFocus
            />
          )}
        </Field>

        <Field label="Wer erledigt das?" hint="Namen eintippen. Du kannst auch mehrere Personen auswählen." error={errors['assigneePersonIds']}>
          {(id) => <PersonPicker id={id} value={assignees} onChange={setAssignees} />}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Bis wann?" error={errors['dueAt']}>
            {(id) => (
              <Input id={id} type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            )}
          </Field>
          {task && <Field label="Status">
            {(id) => (
              <Select
                id={id}
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
              >
                {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>}
        </div>

        <Field label="Für welchen Bereich?" required error={errors['organisationUnitIds']}>
          {() => <OrgUnitPicker value={unitIds} onChange={setUnitIds} />}
        </Field>

        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          aria-expanded={advanced}
          className="min-h-11 text-sm font-medium text-brand-700 hover:underline"
        >
          {advanced ? 'Weitere Angaben ausblenden' : 'Weitere Angaben'}
        </button>

        {advanced && (
          <div className="space-y-4 rounded-lg bg-slate-50 p-3">
            {!task && <Field label="Status" error={errors['status']}>
              {(id) => <Select id={id} value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
                {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>}
            </Field>}
            <Field label="Beschreibung" error={errors['description']}>
              {(id) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Priorität" error={errors['priority']}>
                {(id) => (
                  <Select
                    id={id}
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as TaskPriority)}
                  >
                    {Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Startdatum" error={errors['startAt']}>
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Wiederholung" error={errors['recurrence']}>
                {(id) => (
                  <Select
                    id={id}
                    value={recurrence}
                    onChange={(e) => setRecurrence(e.target.value)}
                  >
                    <option value="NONE">Keine</option>
                    <option value="DAILY">Täglich</option>
                    <option value="WEEKLY">Wöchentlich</option>
                    <option value="MONTHLY">Monatlich</option>
                    <option value="YEARLY">Jährlich</option>
                  </Select>
                )}
              </Field>
              <Field label="Schlagwörter" hint="Durch Komma getrennt" error={errors['tags']}>
                {(id) => (
                  <Input id={id} value={tags} onChange={(e) => setTags(e.target.value)} />
                )}
              </Field>
            </div>
            <Field label="Anhänge" hint="Dateien aus der Mediathek oder aus Wissen & Dokumente" error={errors['attachments']}>
              {(id) => <AttachmentPicker id={id} value={attachments} onChange={setAttachments} />}
            </Field>
            {!task && (
              <Field label="Checkliste" hint="Ein Punkt pro Zeile">
                {(id) => (
                  <Textarea
                    id={id}
                    rows={3}
                    value={checklist}
                    onChange={(e) => setChecklist(e.target.value)}
                  />
                )}
              </Field>
            )}
          </div>
        )}
      </fieldset>
    </Dialog>
  );
}
